import {
  Alert,
  AlertIcon,
  Avatar as TeacherAvatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Image,
  Input,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import Avatar from 'react-avatar-edit';
import {
  fetchTeacher,
  fetchTeachers,
  Teacher,
  updateTeacherImage
} from '../../utils/clients/EstudiAdminClient';
import { useAdminAuthContext } from '../../contexts/AdminAuthProvider';

const getFullName = (teacher: Teacher) => `${teacher.first_name} ${teacher.last_name}`;

const matchesSearch = (teacher: Teacher, search: string) =>
  getFullName(teacher).toLowerCase().includes(search.trim().toLowerCase()) ||
  teacher.external_id.toLowerCase().includes(search.trim().toLowerCase());

const TeacherPicture = () => {
  const toast = useToast();
  const { getAccessToken } = useAdminAuthContext();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(true);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | undefined>(undefined);
  const [cropperKey, setCropperKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadTeachers = async () => {
      try {
        const loadedTeachers = await fetchTeachers();

        setTeachers(loadedTeachers.sort((a, b) => getFullName(a).localeCompare(getFullName(b))));
      } catch (error) {
        setHasLoadingError(true);
      } finally {
        setIsLoadingTeachers(false);
      }
    };

    void loadTeachers();
  }, []);

  const filteredTeachers = useMemo(
    () => (search ? teachers.filter((teacher) => matchesSearch(teacher, search)) : teachers),
    [teachers, search]
  );

  const handleSelect = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setCroppedImage(undefined);
    setCropperKey((key) => key + 1);
  };

  const handleSubmit = async () => {
    const accessToken = getAccessToken();

    if (!selectedTeacher || !croppedImage || !accessToken) {
      return;
    }

    setIsSubmitting(true);

    try {
      await updateTeacherImage(selectedTeacher.external_id, croppedImage, accessToken);

      const updatedTeacher = await fetchTeacher(selectedTeacher.external_id);

      setSelectedTeacher(updatedTeacher);
      setTeachers((currentTeachers) =>
        currentTeachers.map((teacher) =>
          teacher.external_id === updatedTeacher.external_id ? updatedTeacher : teacher
        )
      );
      setCroppedImage(undefined);
      setCropperKey((key) => key + 1);

      toast({
        title: 'Éxito',
        description: 'Foto de perfil actualizada correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la foto de perfil',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box borderWidth="1px" borderRadius="lg" p={8} width="100%">
      <Stack spacing={5}>
        <Text fontSize="xl" fontWeight="bold">
          Cambiar foto de perfil
        </Text>

        {hasLoadingError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            No se pudo cargar el listado de profesores
          </Alert>
        )}

        <FormControl>
          <FormLabel>Profesor</FormLabel>
          <Input
            placeholder="Buscar por nombre o external id"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <FormHelperText textAlign="left">
            {isLoadingTeachers
              ? 'Cargando profesores...'
              : `${filteredTeachers.length} de ${teachers.length} profesores`}
          </FormHelperText>
        </FormControl>

        {isLoadingTeachers && <Spinner alignSelf="center" />}

        {!isLoadingTeachers && (
          <VStack
            align="stretch"
            spacing={0}
            maxHeight="260px"
            overflowY="auto"
            borderWidth="1px"
            borderRadius="md">
            {filteredTeachers.map((teacher) => (
              <HStack
                key={teacher.external_id}
                as="button"
                type="button"
                spacing={3}
                px={3}
                py={2}
                textAlign="left"
                borderBottomWidth="1px"
                bg={
                  selectedTeacher?.external_id === teacher.external_id ? 'blue.50' : 'transparent'
                }
                _hover={{ bg: 'gray.50' }}
                onClick={() => handleSelect(teacher)}>
                <TeacherAvatar
                  size="sm"
                  name={getFullName(teacher)}
                  src={teacher.image_url}
                  borderRadius="md"
                />
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium">
                    {getFullName(teacher)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {teacher.external_id}
                  </Text>
                </Box>
                <Badge colorScheme={teacher.status === 'ACTIVE' ? 'green' : 'gray'}>
                  {teacher.status}
                </Badge>
              </HStack>
            ))}
          </VStack>
        )}

        {selectedTeacher && (
          <React.Fragment>
            <Divider />
            <Text fontWeight="bold">{getFullName(selectedTeacher)}</Text>
            <HStack spacing={8} align="flex-start" justify="center" wrap="wrap">
              <VStack spacing={2}>
                <Text fontSize="sm" color="gray.600">
                  Foto actual
                </Text>
                <Image
                  src={selectedTeacher.image_url}
                  alt={getFullName(selectedTeacher)}
                  boxSize="150px"
                  objectFit="cover"
                  borderRadius="md"
                />
              </VStack>
              {croppedImage && (
                <VStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">
                    Foto nueva
                  </Text>
                  <Image
                    src={croppedImage}
                    alt="Nueva foto de perfil"
                    boxSize="150px"
                    objectFit="cover"
                    borderRadius="md"
                  />
                </VStack>
              )}
            </HStack>

            <FormControl>
              <FormLabel>Nueva foto</FormLabel>
              <FormHelperText textAlign="left">
                El recorte se exporta cuadrado en 400x400, igual que en el alta.
              </FormHelperText>
              <Container display="flex" justifyContent="center" mt={5} width="100%">
                <Avatar
                  key={cropperKey}
                  width={300}
                  height={300}
                  exportSize={400}
                  onCrop={(cropPicture: string) => setCroppedImage(cropPicture)}
                  onClose={() => setCroppedImage(undefined)}
                  label="Elegir una foto"
                  exportAsSquare
                />
              </Container>
            </FormControl>

            <Button
              colorScheme="blue"
              isLoading={isSubmitting}
              loadingText="Guardando..."
              isDisabled={!croppedImage || isSubmitting}
              onClick={handleSubmit}>
              Guardar foto
            </Button>
          </React.Fragment>
        )}
      </Stack>
    </Box>
  );
};

export default TeacherPicture;
